// @vendors
const React = require('react');
const moment = require('moment');
const trim = require('lodash/string/trim');

// @commons
const { OKLink } = require('commonsComponents/OKLink');

// @utilities
const { getPersonTypeAndCode } = require('utilities/hash');

// @helpers
const { FormattedText, formatText } = require('core/i18n').i18n;

// @components
const FormattedTextWithComponents = require('components/broker/brokerFormattedTextWithComponents/brokerFormattedTextWithComponents');

// @constants
const CLIENT_TYPE_PHYSICAL_PERSON = 'F';
const CLIENT_TYPE_LEGAL_PERSON = 'J';

/**
 * @return {string}
 */
function getClientType() {
    const personTypeAndCode = getPersonTypeAndCode();
    return personTypeAndCode.type;
}

/**
 * @param {string | undefined} clientType
 * @returns {bool}
 */
function isClientTypePhysical(clientType = getClientType()) {
    return trim(clientType).toUpperCase() === CLIENT_TYPE_PHYSICAL_PERSON;
}

/**
 * @param {string | undefined} clientType
 * @returns {bool}
 */
function isClientTypeLegal(clientType = getClientType()) {
    return trim(clientType).toUpperCase() === CLIENT_TYPE_LEGAL_PERSON;
}

function buildDocumentationEmailLink() {
    const email = formatText('brokerClientWarningModal-warningEmailId');

    return (
        <a
            className="ok-link ok-link--complementary"
            target="_blank"
            href={`mailto:${email}`}>
            <FormattedText value="brokerClientWarningModal-warningEmailId"/>
        </a>
    );
}

function buildPhysicalPersonBefore2ndJan(asReactComponent) {
    const translation = 'brokerClientWarningModal-warningPhysicalPersonBefore2ndJan_1';
    const mifidDirectiveValidNext3rdJan2018 = 'brokerClientWarningModal-mifidDirectiveValidNext3rdJan2018';
    const noOperationsUntilValidDocument1 = 'brokerClientWarningModal-before3rdJanNoOperationsUntilValidDocument_1';
    const noOperationsUntilValidDocument2 = 'brokerClientWarningModal-before3thJanNoOperationsUntilValidDocument_2';
    const sendDocumentationTo = 'brokerClientWarningModal-sendDocumentationTo';
    const forMoreInformation = 'brokerClientWarningModal-forMoreInformation';
    const warningEmailId = 'brokerClientWarningModal-warningEmailId';

    if (asReactComponent) {
        return (
            <div>
                <p>
                    <FormattedTextWithComponents
                        value={translation}
                        injectedComponents={[
                            <b key={0}><FormattedText value={mifidDirectiveValidNext3rdJan2018}/></b>
                        ]}
                    />
                </p>
                <p>
                    <FormattedTextWithComponents
                        value={noOperationsUntilValidDocument1}
                        injectedComponents={[
                            <b key={0}><FormattedText value={noOperationsUntilValidDocument2}/></b>,
                            <FormattedTextWithComponents key={1} value={sendDocumentationTo} injectedComponents={[
                                buildDocumentationEmailLink()
                            ]}/>
                        ]}
                    />
                </p>
                <p>
                    <FormattedTextWithComponents
                        value={forMoreInformation}
                        injectedComponents={[
                            buildDocumentationEmailLink()
                        ]}
                    />
                </p>
            </div>
        );
    } else {
        return (
            formatText(translation, [formatText(mifidDirectiveValidNext3rdJan2018)]) + '. ' +
            formatText(noOperationsUntilValidDocument1, [
                formatText(noOperationsUntilValidDocument2),
                formatText(sendDocumentationTo, [formatText(warningEmailId)])
            ]) + '. ' +
            formatText(forMoreInformation, [formatText(warningEmailId)])
        );
    }
}

function buildLegalPersonBefore2ndJan(asReactComponent) {
    const translation = 'brokerClientWarningModal-warningLegalPersonBefore2ndJan_1';
    const mifidDirectiveValidNext3rdJan2018 = 'brokerClientWarningModal-mifidDirectiveValidNext3rdJan2018';
    const leiCode = 'brokerClientWarningModal-leiCode';

    const noOperationsUntilValidDocument1 = 'brokerClientWarningModal-before3rdJanNoOperationsUntilValidDocument_1';
    const warningLegalPersonBefore2ndJan2 = 'brokerClientWarningModal-warningLegalPersonBefore2ndJan_2';
    const sendDocumentationTo = 'brokerClientWarningModal-sendDocumentationTo';

    const warningLegalPersonBefore2ndJan3 = 'brokerClientWarningModal-warningLegalPersonBefore2ndJan_3';

    const warningLegalPersonBefore2ndJan4 = 'brokerClientWarningModal-warningLegalPersonBefore2ndJan_4';
    const warningLegalPersonBefore2ndJan5 = 'brokerClientWarningModal-warningLegalPersonBefore2ndJan_5';

    const forMoreInformation = 'brokerClientWarningModal-forMoreInformation';
    const warningEmailId = 'brokerClientWarningModal-warningEmailId';

    if (asReactComponent) {
        return (
            <div>
                <p>
                    <FormattedTextWithComponents
                        value={translation}
                        injectedComponents={[
                            <b key={0}><FormattedText value={mifidDirectiveValidNext3rdJan2018}/></b>,
                            <b key={1}><FormattedText value={leiCode}/></b>
                        ]}
                    />
                </p>
                <p>
                    <FormattedTextWithComponents
                        value={noOperationsUntilValidDocument1}
                        injectedComponents={[
                            <b key={0}><FormattedText value={warningLegalPersonBefore2ndJan2}/></b>,
                            <FormattedTextWithComponents key={1} value={sendDocumentationTo} injectedComponents={[
                                buildDocumentationEmailLink()
                            ]}/>
                        ]}
                    />
                </p>
                <p><FormattedText value={warningLegalPersonBefore2ndJan3}/></p>
                <p>
                    <FormattedText value={warningLegalPersonBefore2ndJan4}/>
                    <br/>
                    <OKLink modifier="complementary" to={formatText(warningLegalPersonBefore2ndJan5)} target="_blank">
                        <FormattedText value={warningLegalPersonBefore2ndJan5}/>
                    </OKLink>
                </p>
                <p>
                    <FormattedTextWithComponents
                        value={forMoreInformation}
                        injectedComponents={[
                            buildDocumentationEmailLink()
                        ]}
                    />
                </p>
            </div>
        );
    } else {
        return (
            formatText(translation, [
                formatText(mifidDirectiveValidNext3rdJan2018),
                formatText(leiCode)
            ]) + ' ' +
            formatText(noOperationsUntilValidDocument1, [
                formatText(warningLegalPersonBefore2ndJan2),
                formatText(sendDocumentationTo, [formatText(warningEmailId)])
            ]) + '. ' +
            formatText(warningLegalPersonBefore2ndJan3) + '. ' +
            formatText(warningLegalPersonBefore2ndJan4) + ' ' +
            formatText(warningLegalPersonBefore2ndJan5) + '. ' +
            formatText(forMoreInformation, [formatText(warningEmailId)])
        );
    }
}

function buildPhysicalPersonAfter3thJan(asReactComponent) {
    const translation = 'brokerClientWarningModal-warningPhysicalPersonFrom3rdJan_1';
    const mifidDirectiveValidSince3rdJan2018 = 'brokerClientWarningModal-mifidDirectiveValidSince3rdJan2018';
    const forMoreInformation = 'brokerClientWarningModal-forMoreInformation';

    if (asReactComponent) {
        return (
            <div>
                <p>
                    <FormattedTextWithComponents
                        value={translation}
                        injectedComponents={[
                            <b key={0}><FormattedText value={mifidDirectiveValidSince3rdJan2018}/></b>
                        ]}
                    />
                </p>
                <p>
                    <FormattedTextWithComponents
                        value={forMoreInformation}
                    />
                </p>
            </div>
        );
    } else {
        return (
            formatText(translation, [formatText(mifidDirectiveValidSince3rdJan2018)]) + ' '  +
            formatText(forMoreInformation)
        );
    }
}

function buildLegalPersonAfter3thJan(asReactComponent) {
    const translation = 'brokerClientWarningModal-waningLegalPersonFrom3rdJan_1';
    const mifidDirectiveValidSince3rdJan2018 = 'brokerClientWarningModal-mifidDirectiveValidSince3rdJan2018';
    const leiCode = 'brokerClientWarningModal-leiCode';

    const forMoreInformation = 'brokerClientWarningModal-forMoreInformation';

    if (asReactComponent) {
        return (
            <div>
                <p>
                    <FormattedTextWithComponents
                        value={translation}
                        injectedComponents={[
                            <b key={0}><FormattedText value={mifidDirectiveValidSince3rdJan2018}/></b>,
                            <b key={1}><FormattedText value={leiCode}/></b>
                        ]}
                    />
                </p>
                <p>
                    <FormattedTextWithComponents
                        value={forMoreInformation}
                    />
                </p>
            </div>
        );
    } else {
        return (
            formatText(translation, [
                formatText(mifidDirectiveValidSince3rdJan2018),
                formatText(leiCode)
            ]) + ' ' +
            formatText(forMoreInformation)
        );
    }
}

/**
 *
 * @param {boolean} asReactComponent False to get an string representation
 * @param {date | string | number | moment} date Optional (today as default)
 * @param {string} clienType Optional (fetched automatically from logged user)
 * @return {ReactNode | string}
 */
function getLeiValidationMessage(
    asReactComponent,
    date = Date.now(),
    clientType = getClientType()
) {
    const sameOrBefore2ndJan = moment(date).isSameOrBefore('2018-01-02', 'day');
    const sameOrAfter3thJan = moment(date).isSameOrAfter('2018-01-03', 'day');

    const isPhysicalPerson = isClientTypePhysical(clientType);
    const isLegalPerson = isClientTypeLegal(clientType);

    let result;

    if (sameOrBefore2ndJan && isPhysicalPerson) {
        result = buildPhysicalPersonBefore2ndJan(asReactComponent);
    } else if (sameOrBefore2ndJan && isLegalPerson) {
        result = buildLegalPersonBefore2ndJan(asReactComponent);
    } else if (sameOrAfter3thJan && isPhysicalPerson) {
        result = buildPhysicalPersonAfter3thJan(asReactComponent);
    } else if (sameOrAfter3thJan && isLegalPerson) {
        result = buildLegalPersonAfter3thJan(asReactComponent);
    } else {
        result = asReactComponent ? <div/> : '';
    }

    return result;
}

function getLeiValidationMessageComponent(date, clientType) {
    return getLeiValidationMessage(true, date, clientType);
}

function getLeiValidationMessageString(date, clientType) {
    return getLeiValidationMessage(false, date, clientType);
}

module.exports = {
    getClientType,
    isClientTypePhysical,
    isClientTypeLegal,
    getLeiValidationMessage,
    getLeiValidationMessageComponent,
    getLeiValidationMessageString
};
